import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import {
    listRegistrations, updateRegistrationStatus, deleteRegistration,
    adminStats, API_BASE,
} from "../lib/api";
import { toast } from "sonner";
import { Download, Search, Trash2, LogOut, RefreshCw } from "lucide-react";

const STATUSES = [
    { v: "iscritto", l: "Iscritto", tone: "bg-sky text-blue-700" },
    { v: "confermato", l: "Confermato", tone: "bg-mint text-emerald-700" },
    { v: "presente", l: "Presente", tone: "bg-gold-100 text-amber-800" },
    { v: "assente", l: "Assente", tone: "bg-navy-100 text-navy-700" },
    { v: "annullato", l: "Annullato", tone: "bg-peach text-red-700" },
];

const INTERESSI = {
    controllo_scadenze: "Controllo scadenze",
    formazione: "Formazione",
    sorveglianza_sanitaria: "Sorveglianza sanitaria",
    sopralluoghi: "Sopralluoghi",
    documenti_report: "Documenti / Report",
    tutte: "Tutte le funzionalità",
};

export default function AdminDashboard() {
    const nav = useNavigate();
    const [token, setToken] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState(null);
    const [q, setQ] = useState("");
    const [interesse, setInteresse] = useState("");
    const [stato, setStato] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        document.title = "Iscrizioni admin | SafetyDesk81";
        const t = localStorage.getItem("sd81_admin_token");
        if (!t) { nav("/admin/login"); return; }
        setToken(t);
    }, [nav]);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (interesse) params.interesse = interesse;
            if (stato) params.stato = stato;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const data = await listRegistrations(token, params);
            setItems(data.items);
            setTotal(data.total);
            const s = await adminStats(token);
            setStats(s);
        } catch (err) {
            if (err?.response?.status === 401) {
                localStorage.removeItem("sd81_admin_token");
                nav("/admin/login");
            } else {
                toast.error("Errore nel caricamento");
            }
        } finally {
            setLoading(false);
        }
    }, [token, q, interesse, stato, dateFrom, dateTo, nav]);

    useEffect(() => { load(); }, [load]);

    const changeStatus = async (id, newStato) => {
        try {
            await updateRegistrationStatus(token, id, newStato);
            setItems((arr) => arr.map((r) => (r.id === id ? { ...r, stato: newStato } : r)));
            setSelected((s) => (s && s.id === id ? { ...s, stato: newStato } : s));
            toast.success("Stato aggiornato");
        } catch { toast.error("Errore aggiornamento"); }
    };

    const removeItem = async (id) => {
        if (!window.confirm("Eliminare definitivamente questa iscrizione?")) return;
        try {
            await deleteRegistration(token, id);
            setItems((arr) => arr.filter((r) => r.id !== id));
            setSelected(null);
            toast.success("Iscrizione eliminata");
        } catch { toast.error("Errore eliminazione"); }
    };

    const download = (format) => {
        const url = `${API_BASE}/admin/registrations/export?format=${format}`;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((b) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(b);
                link.download = format === "csv" ? "iscrizioni_safetydesk81.csv" : "iscrizioni_safetydesk81.xlsx";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .catch(() => toast.error("Errore export"));
    };

    const logout = () => {
        localStorage.removeItem("sd81_admin_token");
        nav("/admin/login");
    };

    return (
        <div className="min-h-screen bg-navy-50">
            <header className="bg-white border-b border-navy-100 sticky top-0 z-30">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <Logo size="sm" />
                        <div className="hidden md:block text-xs">
                            <span className="uppercase tracking-widest text-navy-500 font-semibold">Admin</span>
                            <span className="mx-2 text-navy-300">/</span>
                            <span className="text-gold-500 font-medium">Iscrizioni evento</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={load} data-testid="admin-refresh" className="text-navy-600 hover:text-navy-950 p-2 rounded-full hover:bg-navy-50"><RefreshCw size={16} /></button>
                        <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-xs text-navy-700 hover:text-navy-950 px-3 py-1.5 rounded-full border border-navy-100 hover:bg-navy-50">
                            <LogOut size={14} /> Esci
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-5 md:px-8 py-8">
                <h1 className="font-display font-black text-navy-950 text-3xl md:text-4xl" data-testid="admin-title">Iscrizioni all'evento</h1>
                <p className="text-navy-500 text-sm mt-1">Gestisci le iscrizioni ricevute dalla landing.</p>

                {/* Stats */}
                {stats && (
                    <div className="mt-6 grid grid-cols-2 md:grid-cols-6 gap-3">
                        <StatBox label="Totale" value={stats.total} tone="sky" testId="stat-total" />
                        <StatBox label="Iscritti" value={stats.by_status.iscritto} tone="cream" />
                        <StatBox label="Confermati" value={stats.by_status.confermato} tone="mint" />
                        <StatBox label="Presenti" value={stats.by_status.presente} tone="lav" />
                        <StatBox label="Annullati" value={stats.by_status.annullato} tone="peach" />
                        <StatBox label="Marketing opt-in" value={stats.marketing_optins} tone="cream" />
                    </div>
                )}

                {/* Filters */}
                <div className="mt-6 bg-white rounded-2xl border border-navy-100 p-4 flex flex-wrap gap-2 md:gap-3">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
                        <input
                            data-testid="admin-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Cerca per nome, azienda o email…"
                            className="w-full bg-navy-50 rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:bg-white border border-transparent focus:border-navy-200"
                        />
                    </div>
                    <select data-testid="admin-filter-interesse" value={interesse} onChange={(e) => setInteresse(e.target.value)} className="bg-navy-50 rounded-full px-4 py-2 text-sm outline-none">
                        <option value="">Tutti gli interessi</option>
                        {Object.entries(INTERESSI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <select data-testid="admin-filter-stato" value={stato} onChange={(e) => setStato(e.target.value)} className="bg-navy-50 rounded-full px-4 py-2 text-sm outline-none">
                        <option value="">Tutti gli stati</option>
                        {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                    </select>
                    <input data-testid="admin-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-navy-50 rounded-full px-4 py-2 text-sm outline-none" />
                    <input data-testid="admin-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-navy-50 rounded-full px-4 py-2 text-sm outline-none" />

                    <div className="ml-auto flex gap-2">
                        <button data-testid="export-csv" onClick={() => download("csv")} className="inline-flex items-center gap-1.5 text-xs bg-navy-950 text-white px-4 py-2 rounded-full hover:bg-navy-900">
                            <Download size={14} /> CSV
                        </button>
                        <button data-testid="export-xlsx" onClick={() => download("xlsx")} className="inline-flex items-center gap-1.5 text-xs bg-gold-400 text-navy-950 px-4 py-2 rounded-full hover:bg-gold-500 font-semibold">
                            <Download size={14} /> Excel
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="mt-6 bg-white rounded-2xl border border-navy-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-navy-50 text-navy-500 uppercase tracking-widest text-[10px]">
                                <tr>
                                    <th className="text-left px-4 py-3">Nome</th>
                                    <th className="text-left px-4 py-3">Azienda</th>
                                    <th className="text-left px-4 py-3">Ruolo</th>
                                    <th className="text-left px-4 py-3">Email</th>
                                    <th className="text-left px-4 py-3">Data</th>
                                    <th className="text-left px-4 py-3">Interesse</th>
                                    <th className="text-left px-4 py-3">Stato</th>
                                    <th className="text-right px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody data-testid="admin-registrations">
                                {loading && (
                                    <tr><td colSpan={8} className="text-center py-16 text-navy-400">Caricamento…</td></tr>
                                )}
                                {!loading && items.length === 0 && (
                                    <tr><td colSpan={8} className="text-center py-16 text-navy-400">Nessuna iscrizione trovata</td></tr>
                                )}
                                {!loading && items.map((r) => {
                                    const st = STATUSES.find((s) => s.v === r.stato) || STATUSES[0];
                                    return (
                                        <tr key={r.id} className="border-t border-navy-100/70 hover:bg-navy-50/50">
                                            <td className="px-4 py-3">
                                                <button onClick={() => setSelected(r)} className="text-navy-950 font-medium hover:text-gold-600" data-testid={`reg-name-${r.id}`}>
                                                    {r.nome} {r.cognome}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 text-navy-700">{r.azienda}</td>
                                            <td className="px-4 py-3 text-navy-600">{r.ruolo}</td>
                                            <td className="px-4 py-3 text-navy-600">{r.email}</td>
                                            <td className="px-4 py-3 text-navy-500 text-xs whitespace-nowrap">{new Date(r.registered_at).toLocaleString("it-IT")}</td>
                                            <td className="px-4 py-3 text-navy-600 text-xs">{INTERESSI[r.interesse] || "—"}</td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${st.tone}`}>{st.l}</span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <button data-testid={`delete-${r.id}`} onClick={() => removeItem(r.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={15} /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="px-4 py-3 border-t border-navy-100 text-xs text-navy-500 flex justify-between">
                        <span>{items.length} di {total} iscrizioni</span>
                    </div>
                </div>
            </main>

            {/* Detail drawer */}
            {selected && (
                <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setSelected(null)}>
                    <div className="bg-white rounded-3xl max-w-2xl w-full p-8 relative" onClick={(e) => e.stopPropagation()} data-testid="detail-drawer">
                        <button className="absolute top-4 right-4 w-8 h-8 rounded-full bg-navy-50 hover:bg-navy-100 text-navy-500" onClick={() => setSelected(null)}>×</button>
                        <div className="font-mono text-[10px] tracking-widest uppercase text-navy-400">Iscrizione</div>
                        <h3 className="font-display font-black text-navy-950 text-2xl mt-1">{selected.nome} {selected.cognome}</h3>
                        <div className="text-navy-600 text-sm mt-1">{selected.ruolo} • {selected.azienda}</div>

                        <div className="mt-5 flex flex-wrap gap-2">
                            {STATUSES.map((s) => (
                                <button
                                    key={s.v}
                                    onClick={() => changeStatus(selected.id, s.v)}
                                    data-testid={`set-status-${s.v}`}
                                    className={`text-xs px-3 py-1.5 rounded-full font-semibold ${selected.stato === s.v ? "bg-navy-950 text-white" : "bg-navy-50 text-navy-700 hover:bg-navy-100"}`}
                                >{s.l}</button>
                            ))}
                        </div>

                        <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <Info k="Email" v={selected.email} />
                            <Info k="Telefono" v={selected.telefono} />
                            <Info k="Lavoratori" v={selected.numero_lavoratori || "—"} />
                            <Info k="Interesse" v={INTERESSI[selected.interesse] || "—"} />
                            <Info k="Consenso privacy" v={selected.consenso_privacy ? "Sì" : "No"} />
                            <Info k="Consenso marketing" v={selected.consenso_marketing ? "Sì" : "No"} />
                            <Info k="Data privacy" v={new Date(selected.consenso_privacy_at).toLocaleString("it-IT")} />
                            {selected.consenso_marketing_at && <Info k="Data marketing" v={new Date(selected.consenso_marketing_at).toLocaleString("it-IT")} />}
                            <Info k="Sorgente" v={selected.source} full />
                            {selected.note && <Info k="Note" v={selected.note} full />}
                            {selected.utm && Object.keys(selected.utm).length > 0 && (
                                <Info k="UTM" v={Object.entries(selected.utm).filter(([, val]) => val).map(([k, v]) => `${k}=${v}`).join(" · ")} full />
                            )}
                        </dl>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatBox({ label, value, tone, testId }) {
    const bg = { sky: "#E4ECFD", mint: "#DFF3E4", cream: "#FBF3D4", peach: "#FDE3DD", lav: "#EDE3FD" };
    return (
        <div className="rounded-2xl p-4" style={{ background: bg[tone] || "#F4F6FB" }} data-testid={testId}>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-navy-500">{label}</div>
            <div className="mt-1 text-2xl md:text-3xl font-black text-navy-950 font-display">{value ?? 0}</div>
        </div>
    );
}
function Info({ k, v, full }) {
    return (
        <div className={`bg-navy-50 rounded-xl px-4 py-3 ${full ? "sm:col-span-2" : ""}`}>
            <div className="text-[10px] uppercase tracking-widest text-navy-500 font-semibold">{k}</div>
            <div className="text-navy-950 text-sm mt-0.5 break-words">{v}</div>
        </div>
    );
}
